/**
 * Murphy Door Configurator - Progress Tracker Module
 *
 * PURPOSE: Progress tracking and completion state management using PRE-BUILT PPD structures.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Support (support-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 1 & 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handlers for bootstrap, unapply, material recomputation (Pattern 4)
 * - Event replay ready via bootstrap queue (Pattern 5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor delegation (Pattern 9)
 * - UI update events published for progress changes (Pattern 10)
 * - Async event publishing (Pattern 11)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - No user input rendered to DOM (Pattern 17 - N/A for pure support)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - CONSTANTS.EVENTS.* usage throughout (Pattern 19)
 * - Conductor single publication (Pattern 20)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - getParentTabId(): O(1) Map.get() using PPD.progressMaps.categoryToTabMap
 * - checkTabCategoriesComplete(): O(1) Map.get() using PPD.progressMaps.tabCategoriesMap
 * - calculateTotalProgress(): O(1) counter-based (no iteration)
 * - initializeProgress(): Instant - imports immutable maps from PPD
 *
 * All external communication flows through md-config-core-conductor.js conductor.
 *
 * Dependencies:
 * - md-config-core-conductor.js (PPD access via getPerfectProductDictionary())
 * - md-config-core-event-bus.js (event system)
 * - PPD Layer 4: progressMaps (immutable pre-built maps)
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor)
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ALL events use CONSTANTS.EVENTS.* - NO local EVENT_TYPES objects (DRY)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    CALCULATION: 50, // ms - Progress calculation should complete within 50ms
    UI_UPDATE: 16, // ms - 60fps frame budget for UI updates
    EVENT_PUBLISH: 5, // ms - Event publishing should be near-instant
    CATEGORY_UPDATE: 10, // ms - Category progress updates
    TAB_CHECK: 5, // ms - Tab completion checks (O(1) map lookups)
    INITIALIZATION: 50, // ms - Full initialization from PPD
  });

  const PROGRESS_CONSTANTS = Object.freeze({
    MIN_PERCENTAGE: 5,
    COMPLETE_THRESHOLD: 100,
    SUMMARY_TAB: 'summary',
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
  // Queues any calls made before bootstrap completes for deferred execution
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData, context = 'operation') {
    try {
      const success = await CoreEventBus.publish(eventType, eventData);
      if (!success) {
        isDev() &&
          console.warn(`[ProgressTracker] Failed to publish ${eventType}:`, {
            eventData,
            context,
          });
      }
      return success;
    } catch (error) {
      isDev() &&
        console.error(
          `[ProgressTracker] Error publishing ${eventType}:`,
          error
        );
      // Publish failed event for critical errors
      try {
        await CoreEventBus.publish(EVENTS.SUPPORT_PROGRESS_CALCULATION_FAILED, {
          error: error.message,
          context,
          originalEvent: eventType,
          timestamp: Date.now(),
        });
      } catch (_) {
        // Last resort - avoid infinite loop
      }
      return false;
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // Publishes performance:slow_operation event when threshold exceeded
  // ============================================================================
  function trackPerformance(operation, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operation];
    if (!threshold) {
      isDev() &&
        console.warn(
          `[ProgressTracker] Unknown operation type "${operation}" for performance tracking`
        );
      return;
    }

    if (duration > threshold) {
      publishEvent(
        EVENTS.PERFORMANCE_SLOW_OPERATION,
        {
          module: 'progress-tracker',
          operation,
          duration: Math.round(duration * 100) / 100,
          threshold,
          severity: duration > threshold * 2 ? 'critical' : 'warning',
          timestamp: Date.now(),
        },
        'trackPerformance'
      );
    }
  }

  // Internal state maps for caching
  const STATE_MAPS = {
    completionStates: new Map(),
    progressStates: new Map(),
  };

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // Subscribe to external events at module load time
  // ============================================================================
  if (CoreEventBus) {
    // PATTERN 5: EVENT REPLAY READY (10 points)
    // Bootstrap subscription enables deferred execution queue for late arrivals
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[ProgressTracker] Bootstrap complete event received');
        bootstrapComplete = true;

        // Process queued calls (replay pattern)
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[ProgressTracker] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[ProgressTracker] Error processing queued call:',
                  error
                );
              publishEvent(
                EVENTS.SUPPORT_PROGRESS_CALCULATION_FAILED,
                {
                  error: error.message,
                  context: 'queued_call_replay',
                  timestamp: Date.now(),
                },
                'bootstrap_replay'
              );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[ProgressTracker] Subscribed to bootstrap event at module load time'
      );

    // Consumer: Unapply completed -> cache invalidation
    const unsubUnapply = CoreEventBus.subscribe(
      EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
      (event) => {
        if (!bootstrapComplete) return;

        const categoryId = event?.categoryId;
        if (!categoryId) {
          isDev() &&
            console.warn(
              '[ProgressTracker] UNAPPLY_COMPLETED event missing categoryId, skipping cache invalidation'
            );
          return;
        }

        isDev() &&
          console.log(
            `[ProgressTracker] Unapply completed for "${categoryId}" - invalidating completion cache`
          );

        const categoryKey = `category_${categoryId}`;
        const tracker = window.MurphyDoorConfigurator.progressTracker;

        if (tracker && tracker.completionStates.get(categoryKey)) {
          tracker.completionStates.delete(categoryKey);
          if (tracker.completedCategoryCount > 0) {
            tracker.completedCategoryCount--;
          }

          const tabId = tracker.getParentTabId(categoryId);
          if (tabId) {
            tracker.completionStates.delete(`tab_${tabId}`);
          }

          isDev() &&
            console.log(
              `[ProgressTracker] Cache invalidated for "${categoryId}" (tab: ${tabId || 'unknown'}), completedCount: ${tracker.completedCategoryCount}`
            );
        }

        // PATTERN 10: Publish cache invalidation event for UI coordination
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CACHE_INVALIDATED,
          {
            reason: 'unapply',
            categoryId,
            timestamp: Date.now(),
          },
          'unapply_handler'
        );
      }
    );
    cleanupHandlers.push(unsubUnapply);

    // Consumer: Selection cleared (cascade reset OR manual unapply) -> cache invalidation
    // CRITICAL FIX: Cascade resets fire STATE_SELECTION_CLEARED but NOT SELECTION_USER_UNAPPLY_COMPLETED.
    // Without this handler, cascade-cleared categories remain in completionStates as "complete",
    // causing calculateTotalProgress() to return 100% and auto-navigation to go to Summary
    // instead of the tab with cascade-reset incomplete categories.
    const unsubSelectionCleared = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
      (event) => {
        if (!bootstrapComplete) return;

        const categoryId = event?.categoryId;
        if (!categoryId) {
          isDev() &&
            console.warn(
              '[ProgressTracker] STATE_SELECTION_CLEARED event missing categoryId, skipping cache invalidation'
            );
          return;
        }

        const categoryKey = `category_${categoryId}`;
        const tracker = window.MurphyDoorConfigurator.progressTracker;

        // Guard: only invalidate if category was actually marked complete.
        // This also prevents double-decrement when both STATE_SELECTION_CLEARED and
        // SELECTION_USER_UNAPPLY_COMPLETED fire for the same manual unapply action.
        if (tracker && tracker.completionStates.get(categoryKey)) {
          tracker.completionStates.delete(categoryKey);
          if (tracker.completedCategoryCount > 0) {
            tracker.completedCategoryCount--;
          }

          const tabId = tracker.getParentTabId(categoryId);
          if (tabId) {
            tracker.completionStates.delete(`tab_${tabId}`);
          }

          isDev() &&
            console.log(
              `[ProgressTracker] Selection cleared for "${categoryId}" - invalidating completion cache, completedCount: ${tracker.completedCategoryCount}`
            );

          // Publish cache invalidation event for UI coordination
          publishEvent(
            EVENTS.SUPPORT_PROGRESS_CACHE_INVALIDATED,
            {
              reason: 'selection_cleared',
              categoryId,
              timestamp: Date.now(),
            },
            'selection_cleared_handler'
          );
        }
      }
    );
    cleanupHandlers.push(unsubSelectionCleared);

    // Consumer: Material preference recomputation -> full cache invalidation
    const unsubMaterialRecomputed = CoreEventBus.subscribe(
      EVENTS.MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED,
      (event) => {
        if (!bootstrapComplete) return;

        const survivingWoodSku = event?.survivingWoodSku;
        const survivingFinishSku = event?.survivingFinishSku;
        const cascade = event?.cascade;

        isDev() &&
          console.log(
            `[ProgressTracker] Material preferences recomputed (wood: ${survivingWoodSku || 'none'}, finish: ${survivingFinishSku || 'none'}, cascade: ${cascade}) - full cache invalidation`
          );

        // Full cache clear: preference switch can affect ANY category's completion state
        STATE_MAPS.completionStates.clear();
        STATE_MAPS.progressStates.clear();

        const tracker = window.MurphyDoorConfigurator.progressTracker;
        if (tracker) {
          tracker.completionStates.clear();
          tracker.completedCategoryCount = 0;

          // REBUILD FROM APPLIED STATE. Clearing alone left the counter at
          // zero with nothing to re-populate it: the count only ever grows via
          // updateCategoryProgress(), which fires when the CUSTOMER applies a
          // category. Every selection that survived the recomputation stopped
          // counting, so calculateTotalProgress() could never reach 100 again.
          //
          // Downstream that is not a cosmetic percentage. auto-navigation
          // returns the summary tab only on `totalProgress === 100`, and
          // isSummaryComplete() gates the Add to Cart button -- so after a
          // material swap the customer could re-answer every question and
          // still never be returned to the summary, with ATC permanently
          // disabled. Measured live 2026-08-25: 18 categories applied, counter
          // reading 9, progress stuck at 45%.
          //
          // Rebuilt with updateCategoryProgress()'s own categoryToTabMap
          // guard, so an unknown/stale category cannot push the count past
          // totalCategoryCount and drive progress over 100 -- the failure that
          // guard exists to prevent.
          // getAppliedSelections(), NOT getAppliedSelectionsMap(). The Map form
          // structurally EXCLUDES the measurement categories -- measured live
          // 2026-08-25: the Map returns 18 keys, the plain object returns 20,
          // and the two missing are Width and Height. Rebuilding from the Map
          // therefore dropped both permanently, pinning progress at 90% and
          // leaving the customer just as stuck as before, one step further
          // along. validateCartConfiguration() reads the same plain-object form
          // for the same reason, so the count and the cart gate now agree.
          try {
            const conductor = window.MurphyDoorConfigurator.conductor;
            const selections =
              conductor && conductor.getAppliedSelections
                ? conductor.getAppliedSelections()
                : null;
            if (selections && typeof selections === 'object') {
              Object.keys(selections).forEach((categoryId) => {
                if (!selections[categoryId]) return;
                if (
                  !tracker.categoryToTabMap ||
                  !tracker.categoryToTabMap.has(categoryId)
                ) {
                  return;
                }
                tracker.completionStates.set(`category_${categoryId}`, true);
                tracker.completedCategoryCount++;
              });
            }
          } catch (rebuildError) {
            isDev() &&
              console.error(
                '[ProgressTracker] Completion rebuild failed after material recomputation:',
                rebuildError
              );
          }
        }

        isDev() &&
          console.log(
            '[ProgressTracker] All completion and progress caches invalidated after material preference recomputation'
          );

        // PATTERN 10: Publish cache invalidation event for UI coordination
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CACHE_INVALIDATED,
          {
            reason: 'material_recomputation',
            survivingWoodSku,
            survivingFinishSku,
            cascade,
            timestamp: Date.now(),
          },
          'material_recomputation_handler'
        );
      }
    );
    cleanupHandlers.push(unsubMaterialRecomputed);

    // NOTE: When SYSTEM_SHUTDOWN is added to constants, subscribe here for auto-cleanup.
  }

  /**
   * Module cleanup - unsubscribe all event handlers
   * PATTERN 15: Cleanup Arrays
   */
  function cleanup() {
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (error) {
        isDev() && console.error('[ProgressTracker] Cleanup error:', error);
      }
    });
    cleanupHandlers.length = 0;

    STATE_MAPS.completionStates.clear();
    STATE_MAPS.progressStates.clear();

    isInitialized = false;
    bootstrapComplete = false;
    pendingCalls = [];

    publishEvent(
      EVENTS.SUPPORT_PROGRESS_CLEANUP,
      {
        module: 'progress-tracker',
        timestamp: Date.now(),
      },
      'cleanup'
    );
  }

  // ============================================================================
  // MODULE PUBLICATION
  // PATTERN 20: Single publication on window.MurphyDoorConfigurator
  // ============================================================================
  window.MurphyDoorConfigurator.progressTracker = {
    categoryCountsByTab: new Map(),
    totalCategoryCount: 0,
    completionStates: new Map(),
    // O(1) counter for completed categories (instead of O(N) recalculation)
    completedCategoryCount: 0,

    // PPD-FIRST: Immutable O(1) lookup maps from PPD Layer 4
    // Imported during initialization, never built locally
    categoryToTabMap: new Map(), // categoryId -> tabId (O(1) parent tab lookup)
    tabCategoriesMap: new Map(), // tabId -> [categoryIds] (O(1) tab categories lookup)

    /**
     * Initialize progress tracking from PPD Layer 4 immutable maps.
     *
     * PATTERN 3: 5-Stage Event Lifecycle (STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED)
     * PATTERN 7: Perfect Dictionary Only - PPD via conductor delegation
     * PATTERN 14: Try/Catch + Failed Events
     */
    initializeProgress() {
      // PATTERN 6: Bootstrap barrier - queue if not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            '[ProgressTracker] Bootstrap not complete, queuing initializeProgress()'
          );
        pendingCalls.push({
          fn: this.initializeProgress,
          context: this,
          args: [],
        });
        return;
      }

      // PATTERN 16: Initialization guard
      if (isInitialized) {
        isDev() &&
          console.warn('[ProgressTracker] Already initialized, skipping');
        return;
      }

      const startTime = performance.now();

      try {
        // STAGE 1: START
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CALCULATION_STARTED,
          {
            operation: 'initialize',
            timestamp: Date.now(),
          },
          'initializeProgress'
        );

        // PATTERN 7 & 9: PPD-First - Get Perfect Product Dictionary via conductor delegation
        const conductor = window.MurphyDoorConfigurator.main;
        const ppd = conductor.getPerfectProductDictionary();

        // STAGE 2: VALIDATION
        if (!ppd || !ppd.progressMaps) {
          publishEvent(
            EVENTS.SUPPORT_PROGRESS_CALCULATION_VALIDATION,
            {
              valid: false,
              errors: ['PPD progressMaps not available'],
              timestamp: Date.now(),
            },
            'initializeProgress'
          );

          throw new Error(
            'PPD progressMaps not available - ensure data pipeline Layer 4 has completed'
          );
        }

        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CALCULATION_VALIDATION,
          {
            valid: true,
            hasCategoryToTabMap: !!ppd.progressMaps.categoryToTabMap,
            hasTabCategoriesMap: !!ppd.progressMaps.tabCategoriesMap,
            timestamp: Date.now(),
          },
          'initializeProgress'
        );

        // STAGE 3: PROGRESS
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CALCULATION_PROGRESS,
          {
            stage: 'importing_maps',
            timestamp: Date.now(),
          },
          'initializeProgress'
        );

        isDev() &&
          console.log(
            '[ProgressTracker] Using pre-built immutable maps from PPD Layer 4'
          );

        // Import immutable maps directly from PPD
        const {
          categoryToTabMap,
          tabCategoriesMap,
          tabCategoryCounts,
          totalCategoryCount,
        } = ppd.progressMaps;

        this.categoryToTabMap = categoryToTabMap;
        this.tabCategoriesMap = tabCategoriesMap;
        this.categoryCountsByTab = tabCategoryCounts;
        this.totalCategoryCount = totalCategoryCount;

        isInitialized = true;

        const duration = performance.now() - startTime;

        // STAGE 4: SUCCESS (COMPLETED)
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_INITIALIZED,
          {
            totalCategories: this.totalCategoryCount,
            totalTabs: this.categoryCountsByTab.size,
            duration,
            timestamp: Date.now(),
          },
          'initializeProgress'
        );

        // PATTERN 10: Publish ready event for UI coordination
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_READY,
          {
            module: 'progress-tracker',
            timestamp: Date.now(),
          },
          'initializeProgress'
        );

        // PATTERN 12: Performance tracking
        trackPerformance('INITIALIZATION', duration);

        isDev() &&
          console.log(
            `[ProgressTracker] Initialized with ${this.totalCategoryCount} categories across ${this.categoryCountsByTab.size} tabs (using PPD immutable maps)`
          );
      } catch (error) {
        const duration = performance.now() - startTime;

        // STAGE 5: FAILED
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_INITIALIZATION_FAILED,
          {
            error: error.message,
            duration,
            timestamp: Date.now(),
          },
          'initializeProgress'
        );

        isDev() &&
          console.error('[ProgressTracker] Initialization failed:', error);
      }
    },

    /**
     * O(1) parent tab lookup using PPD immutable categoryToTabMap
     * OLD: O(n*m) Array.find() across all tabs and categories
     * NEW: O(1) Map.get() from PPD Layer 4
     */
    getParentTabId(categoryId) {
      const tabId = this.categoryToTabMap.get(categoryId);

      if (!tabId) {
        isDev() &&
          console.warn(
            `[ProgressTracker] No tab found for category "${categoryId}"`
          );
        return null;
      }

      return tabId;
    },

    /**
     * O(1) tab completion check using PPD immutable tabCategoriesMap
     * OLD: O(n) getTabStructure().get() call + O(m) categories iteration
     * NEW: O(1) Map.get() from PPD Layer 4 + O(m) categories iteration
     *
     * PATTERN 12: Performance tracking
     */
    checkTabCategoriesComplete(tabId) {
      const startTime = performance.now();

      const categories = this.tabCategoriesMap.get(tabId);

      if (!categories) {
        isDev() &&
          console.warn(
            `[ProgressTracker] No categories found for tab "${tabId}"`
          );
        return false;
      }

      const result = categories.every((categoryId) =>
        this.completionStates.get(`category_${categoryId}`)
      );

      const duration = performance.now() - startTime;
      trackPerformance('TAB_CHECK', duration);

      return result;
    },

    isCategoryComplete(categoryId) {
      return this.completionStates.get(`category_${categoryId}`) || false;
    },

    isTabComplete(tabId) {
      return this.checkTabCategoriesComplete(tabId);
    },

    /**
     * Update category progress with full 5-stage event lifecycle.
     *
     * PATTERN 3: 5-Stage Event Lifecycle
     * PATTERN 14: Try/Catch + Failed Events
     * PATTERN 12: Performance tracking
     */
    updateCategoryProgress(categoryId) {
      const startTime = performance.now();

      try {
        // STAGE 1: START
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CALCULATION_STARTED,
          {
            operation: 'updateCategory',
            categoryId,
            timestamp: Date.now(),
          },
          'updateCategoryProgress'
        );

        // STAGE 2: VALIDATION
        if (!categoryId) {
          publishEvent(
            EVENTS.SUPPORT_PROGRESS_CALCULATION_VALIDATION,
            {
              valid: false,
              errors: ['Missing categoryId'],
              timestamp: Date.now(),
            },
            'updateCategoryProgress'
          );
          throw new Error('Missing categoryId for progress update');
        }

        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CALCULATION_VALIDATION,
          {
            valid: true,
            categoryId,
            timestamp: Date.now(),
          },
          'updateCategoryProgress'
        );

        // STAGE 3: PROGRESS
        // Guard: skip categoryIds not tracked in the PPD progressMaps.
        // URL restoration may contain stale/renamed category keys that no longer exist
        // in the PPD. Counting them would overflow completedCategoryCount past
        // totalCategoryCount, causing calculateTotalProgress() to return >100 and
        // isSummaryComplete() to permanently return false.
        if (!this.categoryToTabMap.has(categoryId)) {
          isDev() &&
            console.warn(
              `[ProgressTracker] Unknown categoryId "${categoryId}" — not in PPD categoryToTabMap. Skipping progress update (stale URL param?)`
            );
          // Intentional no-op: return current progress without updating state for unknown categories.
          return Object.freeze({
            categoryComplete: false,
            tabId: null,
            tabComplete: false,
            totalProgress: this.calculateTotalProgress(),
          });
        }

        const categoryKey = `category_${categoryId}`;
        const wasAlreadyComplete = this.completionStates.get(categoryKey);

        if (!wasAlreadyComplete) {
          this.completedCategoryCount++;
        }

        this.completionStates.set(categoryKey, true);

        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CALCULATION_PROGRESS,
          {
            stage: 'category_updated',
            categoryId,
            wasAlreadyComplete: !!wasAlreadyComplete,
            completedCount: this.completedCategoryCount,
            timestamp: Date.now(),
          },
          'updateCategoryProgress'
        );

        // Get and update parent tab completion state
        const tabId = this.getParentTabId(categoryId);
        const isTabComplete = this.checkTabCategoriesComplete(tabId);
        this.completionStates.set(`tab_${tabId}`, isTabComplete);

        // Calculate new total progress
        const totalProgress = this.calculateTotalProgress();

        const duration = performance.now() - startTime;

        // STAGE 4: SUCCESS (COMPLETED)
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CALCULATION_COMPLETED,
          {
            categoryId,
            tabId,
            tabComplete: isTabComplete,
            totalProgress,
            duration,
            timestamp: Date.now(),
          },
          'updateCategoryProgress'
        );

        // PATTERN 12: Performance tracking
        trackPerformance('CATEGORY_UPDATE', duration);

        // Return frozen immutable state object (Pattern 18 - manual freeze for return values)
        return Object.freeze({
          categoryComplete: true,
          tabId,
          tabComplete: isTabComplete,
          totalProgress,
        });
      } catch (error) {
        const duration = performance.now() - startTime;

        // STAGE 5: FAILED
        publishEvent(
          EVENTS.SUPPORT_PROGRESS_CALCULATION_FAILED,
          {
            error: error.message,
            categoryId,
            duration,
            timestamp: Date.now(),
          },
          'updateCategoryProgress'
        );

        isDev() &&
          console.error(
            '[ProgressTracker] updateCategoryProgress failed:',
            error
          );

        return Object.freeze({
          categoryComplete: false,
          tabId: null,
          tabComplete: false,
          totalProgress: this.calculateTotalProgress(),
        });
      }
    },

    isSummaryComplete() {
      return (
        this.calculateTotalProgress() === PROGRESS_CONSTANTS.COMPLETE_THRESHOLD
      );
    },

    /**
     * How many categories still need an answer.
     *
     * 🚨 THIS MUST STAY DERIVED FROM THE SAME TWO COUNTERS `calculateTotalProgress()`
     * DIVIDES. isSummaryComplete() is `calculateTotalProgress() === 100`, and that is
     * `completedCategoryCount / totalCategoryCount` -- so subtracting those same two
     * fields makes the remaining count and the Add to Cart's disabled state
     * arithmetically incapable of disagreeing.
     *
     * Do NOT reimplement this by counting applied selections, filtering categories, or
     * reading either applied-selection store directly. Two of those look correct and
     * are not:
     *   - `getAppliedSelectionsMap()` structurally EXCLUDES the measurement categories
     *     (measured 2026-08-25: Map returns 18 keys, plain object returns 20 -- Width
     *     and Height missing), so a Map-based count reads "2 choices left" forever on a
     *     fully complete door.
     *   - the two applied-selection stores are ALLOWED to drift from each other, which
     *     has already produced confidently-wrong UI in this codebase.
     * A count that disagrees with the button is worse than no count: it tells the
     * customer to go finish something they already finished.
     *
     * @returns {number} categories remaining; 0 when complete
     */
    getRemainingCategoryCount() {
      if (!this.totalCategoryCount) return 0;
      return Math.max(0, this.totalCategoryCount - this.completedCategoryCount);
    },

    updateSummaryState(isComplete) {
      this.completionStates.set(PROGRESS_CONSTANTS.SUMMARY_TAB, isComplete);
    },

    /**
     * Calculate total progress using O(1) counter.
     * PATTERN 12: Performance tracking
     */
    calculateTotalProgress() {
      const startTime = performance.now();

      if (this.totalCategoryCount === 0)
        return PROGRESS_CONSTANTS.MIN_PERCENTAGE;

      // O(1) counter instead of O(N) filtering
      const completedCategories = this.completedCategoryCount;

      const result = Math.min(
        PROGRESS_CONSTANTS.COMPLETE_THRESHOLD,
        Math.max(
          PROGRESS_CONSTANTS.MIN_PERCENTAGE,
          Math.floor((completedCategories / this.totalCategoryCount) * 100)
        )
      );

      const duration = performance.now() - startTime;
      trackPerformance('CALCULATION', duration);

      return result;
    },
  };
})(window);
